// Outils de l'espace comptable : période, formats et libellés.

export const fmt = (n) => new Intl.NumberFormat("fr-FR").format(Math.round(Number(n) || 0));
export const fcfa = (n) => `${fmt(n)} FCFA`;
export const fmtDate = (iso) => (iso ? new Date(iso.length === 10 ? `${iso}T12:00:00` : iso).toLocaleDateString("fr-FR") : "–");

const MOIS = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];
const pad = (n) => String(n).padStart(2, "0");
const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export const moisCourant = (aujourdhui = new Date()) => `${aujourdhui.getFullYear()}-${pad(aujourdhui.getMonth() + 1)}`;
const MOIS_COURTS = ["janv.", "févr.", "mars", "avr.", "mai", "juin", "juil.", "août", "sept.", "oct.", "nov.", "déc."];
export const moisCourt = (yyyymm) => {
  if (!yyyymm) return "";
  const [a, m] = yyyymm.split("-").map(Number);
  return `${MOIS_COURTS[m - 1]} ${String(a).slice(2)}`;
};
export const libelleMois = (yyyymm) => {
  if (!yyyymm) return "";
  const [a, m] = yyyymm.split("-").map(Number);
  return `${MOIS[m - 1]} ${a}`;
};

// Période : { mode: "mois" | "annee" | "intervalle" | "tout", mois, annee, debut, fin }
export const PERIODE_DEFAUT = (aujourdhui = new Date()) => ({
  mode: "mois", mois: moisCourant(aujourdhui), annee: String(aujourdhui.getFullYear()), debut: "", fin: "",
});

export function bornesPeriode(periode) {
  if (periode.mode === "mois" && periode.mois) {
    const [a, m] = periode.mois.split("-").map(Number);
    return { debut: iso(new Date(a, m - 1, 1)), fin: iso(new Date(a, m, 0)), label: libelleMois(periode.mois) };
  }
  if (periode.mode === "annee" && periode.annee) {
    return { debut: `${periode.annee}-01-01`, fin: `${periode.annee}-12-31`, label: `année ${periode.annee}` };
  }
  if (periode.mode === "intervalle" && (periode.debut || periode.fin)) {
    const label = `${periode.debut ? `du ${fmtDate(periode.debut)}` : "jusqu’au"} ${periode.fin ? `${periode.debut ? "au " : ""}${fmtDate(periode.fin)}` : "à aujourd’hui"}`;
    return { debut: periode.debut || "", fin: periode.fin || "", label };
  }
  return { debut: "", fin: "", label: "toutes périodes" };
}

export function parametresPeriode(periode) {
  const { debut, fin } = bornesPeriode(periode);
  const params = new URLSearchParams();
  if (debut) params.set("debut", debut);
  if (fin) params.set("fin", fin);
  const texte = params.toString();
  return texte ? `?${texte}` : "";
}

const CLE_PERIODE = "ql-compta-periode";
export function lirePeriode() {
  try {
    const valeur = JSON.parse(localStorage.getItem(CLE_PERIODE) || "null");
    if (valeur && valeur.mode) return { ...PERIODE_DEFAUT(), ...valeur };
  } catch { /* stockage indisponible */ }
  return PERIODE_DEFAUT();
}
export function ecrirePeriode(periode) {
  try { localStorage.setItem(CLE_PERIODE, JSON.stringify(periode)); } catch { /* stockage indisponible */ }
}

// Navigation depuis une alerte : page + filtre à appliquer à l'arrivée.
const CLE_FOCUS = "ql-compta-focus";
export function ouvrirPage(page, filtre = {}) {
  try { sessionStorage.setItem(CLE_FOCUS, JSON.stringify({ page, filtre })); } catch { /* ignoré */ }
  window.dispatchEvent(new CustomEvent("ql-open-page", { detail: page }));
}
export function lireFocus(page) {
  try {
    const focus = JSON.parse(sessionStorage.getItem(CLE_FOCUS) || "null");
    if (focus && focus.page === page) {
      sessionStorage.removeItem(CLE_FOCUS);
      return focus.filtre || {};
    }
  } catch { /* ignoré */ }
  return null;
}

export const NATURES = [
  ["abonnement", "Abonnement"], ["vente_manuels", "Vente de manuels"], ["prestation", "Prestation"],
  ["apport", "Apport"], ["autre", "Autre"],
];
export const COMPTES = [
  ["banque", "Banque"], ["caisse", "Caisse"], ["orange_money", "Orange Money"], ["mtn_momo", "MTN MoMo"], ["autre", "Autre"],
];
export const ETATS_JUSTIFICATIF = [["joint", "Joint"], ["manquant", "À fournir"], ["non_requis", "Non requis"]];
export const CATEGORIES = [
  ["hebergement", "Hébergement / Serveur"], ["domaine", "Nom de domaine"], ["loyer", "Loyer / Local"],
  ["entretien", "Entretien / Maintenance"], ["marketing", "Marketing / Publicité"], ["evenement", "Événement d'entreprise"],
  ["equipement", "Équipement / Matériel"], ["logiciel", "Logiciel / Abonnement"], ["transport", "Transport / Déplacement"],
  ["formation", "Formation du personnel"], ["fournitures", "Fournitures de bureau"], ["communication", "Communication / Téléphonie"],
  ["impression", "Impression / Reprographie"], ["autre", "Autre"],
];
export const STATUTS_DEPENSE = [["brouillon", "Brouillon"], ["a_valider", "À valider"], ["approuvee", "Approuvée"], ["payee", "Payée"]];
export const STATUTS_SALAIRE = {
  paye: { label: "Payé", ton: "green" },
  partiel: { label: "Partiellement payé", ton: "amber" },
  a_payer: { label: "À payer", ton: "red" },
  non_prevu: { label: "Prévision non renseignée", ton: "grey" },
};

// Méthodes d'encaissement : « paiement manuel » est une méthode de saisie, pas une nature.
export function libelleMethode(methode) {
  const m = String(methode || "");
  const bas = m.toLowerCase();
  if (m === "code_manuel") return "Vente manuel (code)";
  if (m === "GENERATEUR_EPREUVES") return "Portefeuille enseignant";
  if (m === "MANUEL") return "Paiement manuel";
  if (m === "admin_django") return "Admin Django";
  if (m === "AMBASSADEUR") return "Reversement Brand Ambassador";
  if (m === "FINANCEMENT") return "Financement";
  if (m === "SOUTIEN") return "Soutien";
  if (bas.includes("mtn") || bas.includes("mobile")) return "MTN Mobile Money";
  if (bas.includes("orange") || bas.includes("om_cm") || bas === "om") return "Orange Money";
  if (bas.includes("cinetpay")) return "CinetPay";
  return m || "—";
}

export const estManuel = (revenu) => Boolean(revenu.manuel || String(revenu.reference || "").startsWith("MANUEL-"));

export function libelleJustificatif(etat) {
  return (ETATS_JUSTIFICATIF.find(([cle]) => cle === etat) || [null, "Non renseigné"])[1];
}

export const sommer = (liste, cle) => liste.reduce((total, ligne) => total + (Number(ligne[cle]) || 0), 0);

export function repartir(liste, cle, champ, libelles = {}) {
  const groupes = {};
  liste.forEach((ligne) => {
    const k = ligne[cle] || "";
    groupes[k] = (groupes[k] || 0) + (Number(ligne[champ]) || 0);
  });
  return Object.entries(groupes)
    .map(([k, total]) => ({ cle: k, label: libelles[k] || k || "Non renseignée", total }))
    .sort((a, b) => b.total - a.total);
}

export const normaliser = (texte) => String(texte || "").normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
export const correspond = (recherche, ...champs) => {
  const mots = normaliser(recherche).split(/\s+/).filter(Boolean);
  const texte = normaliser(champs.join(" "));
  return mots.every((mot) => texte.includes(mot));
};
